/**
 * Faz 4 / F5 — snapshot tazelik eşiği.
 *
 * v1 kuralı (Faz 4): AYNI GÜN (Europe/Istanbul). Gerekçe: UYAP'ın ücretli sorgu
 * hakkı da günlüktür ve 00:00'da sıfırlanır (bkz. icrapro-app/docs/sorgu-snapshot-sistemi.md §2.3) — gün sınırı
 * doğal eşleşme SANILDI. SAHADA SORUN ÇIKARDI: akşam 20:00 planlanan bir kayıt
 * ertesi gün 11:00'de (kayda 15 saat bile geçmemişken) takvim günü değiştiği
 * için "dün" sayılıp bayatlıyordu — kullanıcı gereksiz yere canlıya düşüp
 * UYAP'a ücretli sorgu ödüyordu.
 *
 * v2 kuralı (KULLANICI KARARI, 9 Eyl 2026): takvim günü DEĞİL, SAAT PENCERESİ.
 * Bir kayıt son görülmesinden (`sonGorulme()` — `fetched_at`/`last_seen_at`
 * ikisinin YENİSİ) itibaren `TAZELIK_PENCERESI_MS` (24 saat) süresince tazedir
 * (bkz. `tazeMi()`). Frekansı 1 günden büyük planlarda (ör. EGM 3 günde bir)
 * sunucu bu pencereyi kayıt bazında `taze_kadar` alanıyla GENİŞLETİR
 * (`SorguSnapshotController` — sonGorulme + frekans_gun × 24 saat); `tazeMi()`
 * bu alanı ÖNCELİKLİ okur, çözülemiyorsa (alan hiçbir aktif planda yoksa ya da
 * damga bozuksa) 24 saatlik sabit pencereye düşer.
 *
 * `istanbulGunu()`/`ayniGunMu()` — v1'in takvim-günü karşılaştırıcıları —
 * ARTIK TAZELİK KARARI VERMEZ (bkz. altlarındaki güncellenmiş docstring'ler):
 * v2 geçişinde üretim çağrı yerinden (`UyapRequest.snapshotCevabiKur`)
 * kaldırıldılar. `ayniGunMu()` yalnız kendi testinde kullanılıyor; ama
 * `istanbulGunu()` hâlâ GERÇEK bir üretim tüketicisine sahip — icrapro-app
 * `sorguKaynagi.ts` (`kaynakYasiMetni()`) rozetin "bugün/dün/N gün önce"
 * metnini bununla üretiyor (tazelik KARARI için değil, yalnız gösterim için).
 * Bu yüzden `istanbulGunu()` "kullanılmayan yardımcı" sanılıp silinmemeli.
 * `geceYarisinaKalanDakika()` (B15, Faz 4, `etkinLocalTtl`'in takvim-günü
 * çapası) aynı gerekçeyle v2'de KALDIRILDI — yerel TTL artık
 * `TAZELIK_PENCERESI_MS`/kaydın `taze_kadar`'ına göre hesaplanıyor
 * (`UyapRequest.etkinLocalTtl()`), gece yarısı sınırının bununla ilgisi kalmadı.
 *
 * Saat dilimi SABİT 'Europe/Istanbul' (`istanbulGunu`/`ayniGunMu` için):
 * kullanıcının makinesi başka bir dilimde olsa bile UYAP'ın günü İstanbul
 * günüdür. Tarayıcı yerel saatine bırakılsaydı yurt dışındaki bir kullanıcıda
 * gün sınırı kayar ve gece koşan veri "dün" sayılıp gereksiz canlı sorgu
 * tetiklenirdi. `tazeMi()`'nin kayan saat penceresi bu kaymadan ETKİLENMEZ
 * (damgalar UTC ISO olarak karşılaştırılır, takvim günü hiç sorulmaz).
 */

const ZAMAN_DILIMI = "Europe/Istanbul";

/**
 * ISO/parse edilebilir bir damgayı İstanbul gününe ("YYYY-MM-DD") indirger.
 * ARTIK TAZELİK KARARI VERMEZ — bkz. dosya başı v1/v2 notu (`tazeMi()` kullanın).
 */
export function istanbulGunu(deger: string | number | Date | null | undefined): string | null {
    if (deger === null || deger === undefined || deger === "") {
        return null;
    }

    const tarih = deger instanceof Date ? deger : new Date(deger as any);
    if (isNaN(tarih.getTime())) {
        return null;
    }

    // 'en-CA' bilerek: ISO düzeninde (YYYY-MM-DD) çıktı verir, elle biçimlendirme
    // gerekmez. Karşılaştırma dize eşitliği olduğu için yerelin adı önemsiz.
    return tarih.toLocaleDateString("en-CA", {timeZone: ZAMAN_DILIMI});
}

/**
 * `damga` İstanbul takviminde `an` ile aynı güne mi düşüyor?
 * Bozuk/eksik damga TAZE DEĞİL sayılır — belirsizlikte canlıya düşülür.
 *
 * ARTIK TAZELİK KARARI VERMEZ: v2 (KULLANICI KARARI, 9 Eyl 2026) ile snapshot
 * tazeliği takvim gününden `tazeMi()`nin SAAT PENCERESİNE geçti; bu fonksiyon
 * üretim çağrı yerinden kaldırıldı, yalnız kendi testleriyle birlikte utility
 * olarak duruyor — bkz. dosya başı not.
 */
export function ayniGunMu(
    damga: string | number | Date | null | undefined,
    an: Date = new Date()
): boolean {
    const gun = istanbulGunu(damga);
    if (gun === null) {
        return false;
    }

    return gun === istanbulGunu(an);
}

/**
 * v2 (KULLANICI KARARI, 9 Eyl 2026) tazelik penceresi: 24 saat, SABİT.
 *
 * `geceYarisinaKalanDakika()` (B15, Faz 4) burada duran eski çapaydı — İstanbul
 * gece yarısına kalan süreyi hesaplayıp yerel TTL'i ona kırpıyordu, çünkü v1
 * tazelik kararı "aynı takvim günü"ydü. v2 artık takvim gününe değil KAYAN bir
 * saat penceresine dayandığı için gece yarısı sınırının pencereyle hiçbir
 * ilgisi kalmadı — fonksiyon KALDIRILDI (üretimde tek çağıranı `UyapRequest.
 * etkinLocalTtl()` idi, o da artık bu sabiti/`tazeMi()`yi kullanıyor).
 */
export const TAZELIK_PENCERESI_MS = 24 * 60 * 60 * 1000;

/** `TAZELIK_PENCERESI_MS`'in dakika karşılığı (1440) — `etkinLocalTtl()`'in "canlı cevap" dalı ve kırpma tavanı için. */
export const TAZELIK_PENCERESI_DK = TAZELIK_PENCERESI_MS / (60 * 1000);

/**
 * Snapshot kaydının tazelik damgası: `fetched_at` ile `last_seen_at`'in YENİSİ.
 *
 * NEDEN İKİSİ: sunucu hash değişmediğinde yeni satır yazmaz, var olan satırın
 * `last_seen_at`'ini günceller (FrekansBekcisi de aynı GREATEST'i kullanıyor).
 * Yalnız `fetched_at`'e bakılsaydı "bugün koştu ama veri değişmedi" durumu bayat
 * görünür ve zincir gereksiz yere canlıya giderdi.
 */
export function sonGorulme(kayit: {fetched_at?: any, last_seen_at?: any} | null | undefined): string | null {
    if (!kayit) {
        return null;
    }

    const damgalar = [kayit.fetched_at, kayit.last_seen_at]
        .map((d) => (d === null || d === undefined || d === "" ? null : new Date(d as any)))
        .filter((d): d is Date => d !== null && !isNaN(d.getTime()));

    if (damgalar.length === 0) {
        return null;
    }

    const enYeni = damgalar.reduce((a, b) => (a.getTime() >= b.getTime() ? a : b));

    return enYeni.toISOString();
}

/**
 * v2 tazelik kararı (KULLANICI KARARI, 9 Eyl 2026 — bkz. dosya başı not).
 *
 * ÖNCELİK `kayit.taze_kadar`: sunucunun (aktif planların EN BÜYÜK frekansına
 * göre) kayıt bazında hesapladığı ISO-8601 damga — çözülebiliyorsa TEK ölçüt
 * budur (`simdi < taze_kadar`). Alan hiçbir aktif planda yoksa sunucu bu alanı
 * `null` gönderir; damga bozuk/parse edilemez geldiyse de aynı şekilde
 * YOK SAYILIR — ikisinde de SABİT 24 saatlik pencereye (`TAZELIK_PENCERESI_MS`)
 * düşülür: `simdi − sonGorulme(kayit) < 24 saat`.
 *
 * Bozuk/eksik `sonGorulme` (kayıtta ne `fetched_at` ne `last_seen_at` çözülüyor)
 * TAZE DEĞİL sayılır — `ayniGunMu()`deki ile aynı "belirsizlikte canlıya düş"
 * ilkesi.
 */
export function tazeMi(
    kayit: {fetched_at?: any, last_seen_at?: any, taze_kadar?: string | null} | null | undefined,
    simdi: Date = new Date()
): boolean {
    if (!kayit) {
        return false;
    }

    if (kayit.taze_kadar) {
        const tazeKadar = new Date(kayit.taze_kadar);
        if (!isNaN(tazeKadar.getTime())) {
            return simdi.getTime() < tazeKadar.getTime();
        }
    }

    const damga = sonGorulme(kayit);
    if (damga === null) {
        return false;
    }

    return simdi.getTime() - new Date(damga).getTime() < TAZELIK_PENCERESI_MS;
}
